import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ShoppingCart,
  Building2,
  User,
  Calendar,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  Package,
  DollarSign,
  FileText,
  RefreshCw,
  Send,
  AlertCircle,
  Trash2,
  Edit,
  Repeat,
  Plus,
  Receipt,
  CreditCard
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getOrderById,
  updateOrderStatus,
  generateProformaInvoice,
  regenerateProformaInvoice,
  sendProformaToWhatsApp,
  recordOrderPayment,
  generateLoadedDocuments
} from '../../api/wholesale/orders';
import {
  getExpenses,
  deleteExpense,
  approveExpense,
  rejectExpense,
  postExpenseToGL
} from '../../api/expenses';
import apiClient from '../../api/client';
import PaymentRecordingModal from '../../components/wholesale/PaymentRecordingModal';
import CompanyConfirmationModal from '../../components/wholesale/CompanyConfirmationModal';
import ProformaAndPaymentModal from '../../components/wholesale/ProformaAndPaymentModal';
import InvoicePreviewModal from '../../components/wholesale/InvoicePreviewModal';
import ProformaInvoicePreviewModal from '../../components/wholesale/ProformaInvoicePreviewModal';
import ReceiptPreviewModal from '../../components/wholesale/ReceiptPreviewModal';
import CreateOrderModal from '../../components/wholesale/CreateOrderModal';
import ReassignOrderModal from '../../components/wholesale/ReassignOrderModal';
import ExpenseFormModal from '../../components/ExpenseFormModal';
import ExpenseDetailModal from '../wholesale/expenses/ExpenseDetailModal';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const WholesaleOrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCompanyConfirmModal, setShowCompanyConfirmModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showProformaPaymentModal, setShowProformaPaymentModal] = useState(false);
  const [generatingProforma, setGeneratingProforma] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProformaPreviewModal, setShowProformaPreviewModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [generatedReceipt, setGeneratedReceipt] = useState(null);
  const [orderExpenses, setOrderExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showExpenseDetailModal, setShowExpenseDetailModal] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';

  const fetchOrder = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const response = await getOrderById(id);
      setOrder(response?.data || response);
    } catch (err) {
      console.error('Error fetching order:', err);
      // If order not found (404), redirect to orders list
      if (err?.response?.status === 404 || err?.status === 404) {
        toast.error('Order not found or has been deleted');
        navigate('/wholesale/orders');
        return;
      }
      setError('Failed to load order details');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const fetchOrderExpenses = async () => {
    try {
      setLoadingExpenses(true);
      const response = await getExpenses({ wholesale_order_id: id, module: 'wholesale' });
      setOrderExpenses(response.data || []);
    } catch (err) {
      console.error('Error fetching order expenses:', err);
    } finally {
      setLoadingExpenses(false);
    }
  };

  // Handler for viewing expense details
  const handleExpenseClick = (expense) => {
    setSelectedExpense(expense);
    setShowExpenseDetailModal(true);
  };

  // Handler for deleting an expense
  const handleDeleteExpense = async (expenseId, expenseNumber) => {
    if (!window.confirm(`Are you sure you want to delete expense ${expenseNumber}?`)) return;

    setDeletingExpense(true);
    try {
      await deleteExpense(expenseId);
      toast.success(`Expense ${expenseNumber} deleted successfully`);
      setShowExpenseDetailModal(false);
      setSelectedExpense(null);
      fetchOrderExpenses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete expense');
    } finally {
      setDeletingExpense(false);
    }
  };

  // Handler for approving an expense
  const handleApproveExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to approve this expense?')) return;

    try {
      await approveExpense(expenseId);
      toast.success('Expense approved successfully');
      setShowExpenseDetailModal(false);
      setSelectedExpense(null);
      fetchOrderExpenses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve expense');
    }
  };

  // Handler for rejecting an expense
  const handleRejectExpense = async (expense) => {
    const reason = window.prompt('Please enter rejection reason:');
    if (!reason || !reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      await rejectExpense(expense.id, reason);
      toast.success('Expense rejected successfully');
      setShowExpenseDetailModal(false);
      setSelectedExpense(null);
      fetchOrderExpenses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject expense');
    }
  };

  // Handler for posting expense to GL
  const handlePostToGL = async (expenseId) => {
    if (!window.confirm('Are you sure you want to post this expense to the General Ledger?'))
      return;

    try {
      await postExpenseToGL(expenseId);
      toast.success('Expense posted to GL successfully');
      setShowExpenseDetailModal(false);
      setSelectedExpense(null);
      fetchOrderExpenses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post expense to GL');
    }
  };

  useEffect(() => {
    fetchOrder();
    fetchOrderExpenses();
  }, [id]);

  const handleStatusUpdate = async (newStatus, notes = null) => {
    setUpdating(true);
    try {
      await updateOrderStatus(id, newStatus, notes);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrder();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleForward = async () => {
    if (!window.confirm('Forward this order to the manufacturer?')) return;
    setUpdating(true);
    try {
      await updateOrderStatus(id, 'forwarded');
      toast.success('Order forwarded to manufacturer');
      fetchOrder();
    } catch (err) {
      toast.error('Failed to forward order');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setUpdating(true);
    try {
      await updateOrderStatus(id, 'cancelled');
      toast.success('Order cancelled successfully');
      fetchOrder();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel order');
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateProforma = async () => {
    setGeneratingProforma(true);
    try {
      const response = await generateProformaInvoice(id);
      toast.success('Proforma invoice generated successfully');
      await fetchOrder(); // Refresh to show proforma invoice number
      // Show proforma preview modal
      setShowProformaPreviewModal(true);
    } catch (err) {
      toast.error(err.message || 'Failed to generate proforma invoice');
    } finally {
      setGeneratingProforma(false);
    }
  };

  const handleRegenerateProforma = async () => {
    if (!window.confirm('Regenerate proforma invoice? This will update the timestamp.')) return;
    setGeneratingProforma(true);
    try {
      const response = await regenerateProformaInvoice(id);
      toast.success('Proforma invoice regenerated successfully');
      await fetchOrder(); // Refresh to show updated timestamp
      // Show proforma preview modal
      setShowProformaPreviewModal(true);
    } catch (err) {
      toast.error(err.message || 'Failed to regenerate proforma invoice');
    } finally {
      setGeneratingProforma(false);
    }
  };

  const handlePaymentSuccess = () => {
    fetchOrder(false); // Refresh order to show payment details
  };

  const handleCompanyConfirm = async () => {
    setUpdating(true);
    try {
      await updateOrderStatus(id, 'company_confirmed');
      toast.success('Order marked as company confirmed');
      await fetchOrder();
      setShowCompanyConfirmModal(false);
      // Open the proforma and payment modal after company confirmation
      setShowProformaPaymentModal(true);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleProformaPaymentSuccess = async (updatedOrder) => {
    // Refresh order data
    if (updatedOrder) {
      setOrder(updatedOrder);
    }
    await fetchOrder(false);
  };

  const handleProceedToLoading = async () => {
    setShowProformaPaymentModal(false);
    setUpdating(true);
    try {
      await updateOrderStatus(id, 'loading');
      toast.success('Order marked as loading');
      await fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkAsLoaded = async () => {
    // No confirmation popup - documents already generated during payment confirmation
    setUpdating(true);
    try {
      // Just update status to loaded - documents already exist from payment confirmation
      await updateOrderStatus(id, 'loaded');
      toast.success('Order marked as loaded');
      await fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark order as loaded');
    } finally {
      setUpdating(false);
    }
  };

  // Handler for generating and viewing invoice at 'loaded' status
  const handleGenerateAndViewInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      // Generate documents (invoice/receipt) based on payment status
      const docsResponse = await generateLoadedDocuments(id);

      // Determine which documents were generated
      const hasInvoice = docsResponse.data?.invoice;
      const hasReceipt = docsResponse.data?.receipt;

      // Store the generated documents
      if (hasInvoice) {
        setGeneratedInvoice(docsResponse.data.invoice);
        toast.success(`Invoice ${docsResponse.data.invoice.invoice_number} generated`);
      }

      if (hasReceipt) {
        setGeneratedReceipt(docsResponse.data.receipt);
        if (!hasInvoice) {
          toast.success(`Receipt ${docsResponse.data.receipt.receipt_number} generated`);
        }
      }

      // Refresh order to get updated document references
      // Use order from response if available, otherwise fetch
      if (docsResponse.data?.order) {
        setOrder(docsResponse.data.order);
      } else {
        await fetchOrder(false);
      }

      // Show the appropriate modal based on what was generated
      // Invoice takes priority for "Generate Invoice" action unless only receipt exists
      if (hasInvoice) {
        setShowInvoiceModal(true);
      } else if (hasReceipt) {
        setShowReceiptModal(true);
      } else {
        toast.error('No invoice or receipt was generated. Please check order payment status.');
      }
    } catch (err) {
      console.error('Error generating documents:', err);
      toast.error(err.response?.data?.error || 'Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Handler for closing invoice modal and proceeding to invoiced status
  const handleInvoiceModalClose = async () => {
    // Fetch fresh order data to ensure we have latest invoice_number
    const currentOrder = await getOrderById(id);
    const latestOrder = currentOrder?.data || currentOrder;

    // Update status to invoiced after viewing invoice (if still at loaded)
    if (latestOrder?.order_status === 'loaded' && latestOrder?.invoice_number) {
      setUpdating(true);
      try {
        await updateOrderStatus(id, 'invoiced');
        toast.success('Order marked as invoiced');
        await fetchOrder();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to update status');
      } finally {
        setUpdating(false);
      }
    }

    // Close modal after status update completes
    setShowInvoiceModal(false);
    setGeneratedInvoice(null);
  };

  // Handler for closing receipt modal and proceeding to invoiced status
  const handleReceiptModalClose = async () => {
    // Fetch fresh order data to ensure we have latest receipt_number
    const currentOrder = await getOrderById(id);
    const latestOrder = currentOrder?.data || currentOrder;

    // Update status to invoiced after viewing receipt (receipts are for paid orders)
    if (latestOrder?.order_status === 'loaded' && latestOrder?.receipt_number) {
      setUpdating(true);
      try {
        await updateOrderStatus(id, 'invoiced');
        toast.success('Order marked as invoiced');
        await fetchOrder();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to update status');
      } finally {
        setUpdating(false);
      }
    }

    // Close modal after status update completes
    setShowReceiptModal(false);
    setGeneratedReceipt(null);
  };

  const handleDeleteOrder = async () => {
    if (!window.confirm('PERMANENTLY DELETE this order? This action cannot be undone!')) return;
    if (!window.confirm('Are you absolutely sure? This will delete all order data.')) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/wholesale/orders/${id}`);
      toast.success('Order deleted successfully');
      navigate('/wholesale/orders');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete order');
    } finally {
      setDeleting(false);
    }
  };

  const handleRecalculateTonnage = async () => {
    setUpdating(true);
    try {
      const response = await apiClient.post(`/wholesale/orders/${id}/recalculate-tonnage`);
      toast.success(response.data?.message || 'Tonnage recalculated');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to recalculate tonnage');
    } finally {
      setUpdating(false);
    }
  };

  const statusConfig = {
    // Status 1: New Order (WhatsApp only)
    new_order: { label: 'New Order', color: 'bg-sky-100 text-sky-700', icon: Clock },
    // Status 2: Parsed (WhatsApp only)
    parsed: { label: 'Parsed', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
    // Status 3: Pending Admin Review
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
    // Status 4: Confirmed (Manual orders start here)
    confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    // Status 5: Forwarded to Company
    forwarded: { label: 'Forwarded', color: 'bg-purple-100 text-purple-700', icon: Send },
    // Status 6: Company Confirmed
    company_confirmed: {
      label: 'Company Confirmed',
      color: 'bg-violet-100 text-violet-700',
      icon: CheckCircle
    },
    // Status 7: Loading (CREDIT CHECK)
    loading: { label: 'Loading', color: 'bg-orange-100 text-orange-700', icon: Truck },
    // Status 8: Loaded
    loaded: { label: 'Loaded', color: 'bg-orange-100 text-orange-700', icon: Package },
    // Status 9: Invoiced
    invoiced: { label: 'Invoiced', color: 'bg-cyan-100 text-cyan-700', icon: FileText },
    // Status 10: Dispatched
    dispatched: { label: 'Dispatched', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
    // Status 11: Delivered
    delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    // Status 12: Completed
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    // Status 13: Cancelled
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
    // Special: On Hold
    on_hold: { label: 'On Hold', color: 'bg-gray-100 text-gray-700', icon: AlertCircle }
  };

  const statusSteps = [
    'pending',
    'confirmed',
    'forwarded',
    'company_confirmed',
    'loading',
    'loaded',
    'invoiced',
    'dispatched',
    'delivered',
    'completed'
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !order) {
    return (
      <div className="space-y-6">
        <Link
          to="/wholesale/orders"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Orders
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Order not found'}
        </div>
      </div>
    );
  }

  const orderStatus = order.order_status || order.status || 'pending';
  const status = statusConfig[orderStatus] || statusConfig.pending;
  const StatusIcon = status.icon;
  const items = order.items || [];
  const total = parseFloat(order.total_amount) || 0;
  const currentStepIndex = statusSteps.indexOf(orderStatus);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/wholesale/orders"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{order.order_number}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}
              >
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </span>
              {/* Payment Status Badge */}
              {(() => {
                const paymentStatus = order.payment_status || 'unpaid';
                const paymentConfig = {
                  paid: { label: 'Paid', color: 'bg-green-100 text-green-700 border-green-200' },
                  credit: {
                    label: 'Credit',
                    color: 'bg-amber-100 text-amber-700 border-amber-200'
                  },
                  partial: {
                    label: 'Partially Paid',
                    color: 'bg-blue-100 text-blue-700 border-blue-200'
                  },
                  unpaid: { label: 'Unpaid', color: 'bg-gray-100 text-gray-500 border-gray-200' },
                  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200' }
                };
                const config = paymentConfig[paymentStatus] || paymentConfig.unpaid;
                return (
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}
                  >
                    {config.label}
                  </span>
                );
              })()}
              {order.order_date && (
                <span className="text-sm text-gray-500">
                  {new Date(order.order_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchOrder}
            className="p-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          {/* Edit Order button - available before loading status */}
          {['pending', 'confirmed', 'forwarded', 'company_confirmed'].includes(orderStatus) && (
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              <Edit className="w-4 h-4" />
              Edit Order
            </button>
          )}

          {/* Generate/Regenerate Proforma Invoice button - available before loading status */}
          {/* Dynamic Document Action Button */}
          {(() => {
            // Check if payment passed (recorded or status indicates payment) OR if documents exist
            const hasPayment =
              order.payment_recorded_at ||
              ['paid', 'partial', 'credit'].includes(order.payment_status) ||
              order.invoice_number ||
              order.receipt_number;

            // CASE 1: No Payment - Show Proforma Actions
            if (!hasPayment) {
              if (
                ['pending', 'confirmed', 'forwarded', 'company_confirmed'].includes(orderStatus)
              ) {
                return (
                  <button
                    onClick={
                      order.proforma_invoice_number
                        ? handleRegenerateProforma
                        : handleGenerateProforma
                    }
                    disabled={generatingProforma}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <FileText className="w-4 h-4" />
                    {generatingProforma
                      ? 'Generating...'
                      : order.proforma_invoice_number
                        ? 'Regenerate Proforma'
                        : 'Generate Proforma'}
                  </button>
                );
              }
              return null;
            }

            // CASE 2: Payment Recorded - Show Invoice/Receipt Actions
            const isFullyPaid = order.payment_status === 'paid';
            const isPartialOrCredit = ['partial', 'credit'].includes(order.payment_status);

            // 2a. Fully Paid -> Receipt
            if (isFullyPaid) {
              // Always use handleGenerateAndViewInvoice which ensures receipt exists
              // (generates if missing, returns existing if present) before opening modal
              return (
                <button
                  onClick={handleGenerateAndViewInvoice}
                  disabled={generatingInvoice}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Receipt className="w-4 h-4" />
                  {generatingInvoice
                    ? 'Loading...'
                    : order.receipt_number
                      ? 'View Receipt'
                      : 'Generate Receipt'}
                </button>
              );
            }

            // 2b. Partial/Credit -> Invoice
            if (isPartialOrCredit) {
              // Always use handleGenerateAndViewInvoice which ensures invoice exists
              // (generates if missing, returns existing if present) before opening modal
              return (
                <button
                  onClick={handleGenerateAndViewInvoice}
                  disabled={generatingInvoice}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  {generatingInvoice
                    ? 'Loading...'
                    : order.invoice_number
                      ? 'View Invoice'
                      : 'Generate Invoice'}
                </button>
              );
            }

            // Fallback: If invoice exists (even if unpaid), allow view
            if (order.invoice_number) {
              return (
                <button
                  onClick={() => setShowInvoiceModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                >
                  <FileText className="w-4 h-4" />
                  View Invoice
                </button>
              );
            }

            return null;
          })()}

          {/* Action buttons based on status */}
          {orderStatus === 'pending' && (
            <button
              onClick={() => handleStatusUpdate('confirmed')}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {updating ? 'Confirming...' : 'Confirm Order'}
            </button>
          )}
          {orderStatus === 'confirmed' && (
            <button
              onClick={handleForward}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {updating
                ? 'Forwarding...'
                : `Forward to ${order.manufacturer_name || order.company_name || 'Manufacturer'}`}
            </button>
          )}
          {orderStatus === 'forwarded' && (
            <button
              onClick={() => setShowCompanyConfirmModal(true)}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              Proceed to Loading
            </button>
          )}
          {orderStatus === 'company_confirmed' && (
            <>
              {!order?.payment_recorded_at ? (
                <button
                  onClick={() => setShowProformaPaymentModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                >
                  <FileText className="w-4 h-4" />
                  Proforma & Payment
                </button>
              ) : (
                <>
                  <button
                    onClick={handleProceedToLoading}
                    disabled={updating}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {updating ? 'Processing...' : 'Proceed to Loading'}
                  </button>
                  {/* Generate Invoice early - available when payment is recorded */}
                  {/* Invoice button moved to top header */}
                </>
              )}
            </>
          )}
          {orderStatus === 'loading' && (
            <>
              <button
                onClick={handleMarkAsLoaded}
                disabled={updating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                {updating ? 'Processing...' : 'Mark as Loaded'}
              </button>
              {/* Generate Invoice - available at loading status */}
              {/* Invoice button moved to top header */}
              <button
                onClick={() => setShowReassignModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-violet-600 text-violet-600 rounded-lg hover:bg-violet-50"
              >
                <Repeat className="w-4 h-4" />
                Reassign Order
              </button>
            </>
          )}
          {orderStatus === 'loaded' && (
            <button
              onClick={() => handleStatusUpdate('invoiced')}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {updating ? 'Updating...' : 'Mark as Invoiced'}
            </button>
          )}
          {orderStatus === 'invoiced' && (
            <button
              onClick={() => handleStatusUpdate('dispatched')}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Truck className="w-4 h-4" />
              {updating ? 'Updating...' : 'Mark as Dispatched'}
            </button>
          )}
          {orderStatus === 'dispatched' && (
            <button
              onClick={() => handleStatusUpdate('delivered')}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {updating ? 'Updating...' : 'Mark Delivered'}
            </button>
          )}
          {orderStatus === 'delivered' && (
            <button
              onClick={() => handleStatusUpdate('completed')}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {updating ? 'Completing...' : 'Complete Order'}
            </button>
          )}
        </div>
      </div>

      {/* Status Timeline */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Order Progress</h3>
        <div className="flex items-center justify-between overflow-x-auto pb-2">
          {statusSteps.slice(0, 6).map((step, index) => {
            const stepConfig = statusConfig[step];
            const isActive = currentStepIndex >= index;
            const isCurrent = orderStatus === step;
            return (
              <div key={step} className="flex flex-col items-center min-w-[80px]">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'
                  } ${isCurrent ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`}
                >
                  {index + 1}
                </div>
                <span
                  className={`text-xs mt-2 text-center ${isActive ? 'text-emerald-600 font-medium' : 'text-gray-500'}`}
                >
                  {stepConfig.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Order Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer */}
        {/* Customer & Manufacturer Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Customer
            </h3>
            <p className="font-semibold text-gray-900">
              {order.distributor_business_name ||
                order.business_name ||
                order.distributor_name ||
                '-'}
            </p>
            <p className="text-sm text-gray-500 mt-1">{order.contact_person_name || ''}</p>
            {order.delivery_address && (
              <p className="text-sm text-gray-500 mt-2">{order.delivery_address}</p>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Manufacturing Company
            </h3>
            <p className="font-medium text-gray-900">
              {order.manufacturer_name || order.company_name || '-'}
            </p>
            {order.is_ftz && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mt-1">
                <CheckCircle className="w-3 h-3" />
                FTZ
              </span>
            )}
          </div>
        </div>

        {/* Payment Summary Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment Summary
          </h3>

          {/* Payment Status Badge */}
          <div className="mb-4">
            {(() => {
              const paymentStatus = order.payment_status || 'unpaid';
              const paymentConfig = {
                paid: {
                  label: 'Fully Paid',
                  color: 'bg-green-100 text-green-700 border-green-200',
                  icon: CheckCircle
                },
                credit: {
                  label: 'On Credit',
                  color: 'bg-amber-100 text-amber-700 border-amber-200',
                  icon: Clock
                },
                partial: {
                  label: 'Partially Paid',
                  color: 'bg-blue-100 text-blue-700 border-blue-200',
                  icon: DollarSign
                },
                unpaid: {
                  label: 'Unpaid',
                  color: 'bg-red-50 text-red-700 border-red-200',
                  icon: AlertCircle
                },
                overdue: {
                  label: 'Overdue',
                  color: 'bg-red-100 text-red-700 border-red-200',
                  icon: AlertCircle
                }
              };
              const config = paymentConfig[paymentStatus] || paymentConfig.unpaid;
              const StatusIcon = config.icon;

              return (
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.color}`}
                >
                  <StatusIcon className="w-4 h-4" />
                  <span className="text-sm font-medium">{config.label}</span>
                </div>
              );
            })()}
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Total Amount</span>
              <span className="font-semibold text-gray-900">
                ₦{parseFloat(order.total_amount || 0).toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Amount Paid</span>
              <span className="font-medium text-emerald-600">
                ₦{parseFloat(order.amount_paid || 0).toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-700">Balance Due</span>
              <span
                className={`font-bold ${parseFloat(order.balance_due || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}
              >
                ₦{parseFloat(order.balance_due || 0).toLocaleString()}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Payment Progress</span>
                <span>
                  {order.total_amount > 0
                    ? Math.min(
                        100,
                        Math.round(
                          (parseFloat(order.amount_paid || 0) / parseFloat(order.total_amount)) *
                            100
                        )
                      )
                    : 0}
                  %
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    order.payment_status === 'paid'
                      ? 'bg-green-500'
                      : order.payment_status === 'credit'
                        ? 'bg-amber-500'
                        : 'bg-blue-500'
                  }`}
                  style={{
                    width: `${order.total_amount > 0 ? Math.min(100, (parseFloat(order.amount_paid || 0) / parseFloat(order.total_amount)) * 100) : 0}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Order Summary
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Items</span>
              <span className="font-medium text-gray-900">{items.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Total Cartons</span>
              <span className="font-medium text-gray-900">
                {items
                  .reduce(
                    (sum, item) =>
                      sum + (parseFloat(item.quantity) || parseFloat(item.quantity_cartons) || 0),
                    0
                  )
                  .toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Total Tonnage</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {order.total_tonnage ? `${order.total_tonnage.toFixed(2)} tons` : '- tons'}
                </span>
                {(!order.total_tonnage || order.total_tonnage === 0) && (
                  <button
                    onClick={handleRecalculateTonnage}
                    disabled={updating}
                    className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                  >
                    {updating ? '...' : 'Recalculate'}
                  </button>
                )}
              </div>
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-100">
              <span className="font-medium text-gray-900">Total</span>
              <span className="font-bold text-emerald-600 text-lg">₦{total.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-gray-500" />
            Order Items
          </h3>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No items in this order</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Product</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Code</th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Cartons
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Tonnage
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">
                    Unit Price
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, index) => {
                  const qty = parseFloat(item.quantity) || parseFloat(item.quantity_cartons) || 0;
                  const price =
                    parseFloat(item.final_price_per_unit) ||
                    parseFloat(item.base_price_per_unit) ||
                    parseFloat(item.unit_price) ||
                    0;
                  const lineTotal = parseFloat(item.line_subtotal) || qty * price;
                  const lineTonnage =
                    parseFloat(item.line_tonnage) ||
                    (qty * parseFloat(item.weight_per_unit || 0)) / 1000;
                  return (
                    <tr key={item.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {item.product_name || item.description || 'Product'}
                      </td>
                      <td className="px-6 py-4 text-gray-500">{item.product_code || '-'}</td>
                      <td className="px-6 py-4 text-right text-gray-900">{qty.toLocaleString()}</td>
                      <td className="px-6 py-4 text-right text-gray-600">
                        {lineTonnage > 0 ? `${lineTonnage.toFixed(3)} t` : '-'}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900">
                        ₦{price.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        ₦{lineTotal.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="4" className="px-6 py-3 text-right font-semibold text-gray-900">
                    Order Total
                  </td>
                  <td className="px-6 py-3 text-right font-bold text-emerald-600 text-lg">
                    ₦{total.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            Order Notes
          </h3>
          <p className="text-gray-700">{order.notes}</p>
        </div>
      )}

      {/* Order Expenses Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Receipt className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            Order Expenses (Accounting)
          </h3>
          <button
            onClick={() => setShowExpenseModal(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Expenses linked to this order are for internal accounting only. They are not included in
            customer documents.
          </p>

          {loadingExpenses ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              Loading expenses...
            </div>
          ) : orderExpenses.length === 0 ? (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              No expenses linked to this order yet.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto mb-4">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Expense #
                      </th>
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Category
                      </th>
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Description
                      </th>
                      <th className="text-right px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Amount
                      </th>
                      <th className="text-left px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {orderExpenses.map((expense) => (
                      <tr
                        key={expense.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
                        onClick={() => handleExpenseClick(expense)}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {expense.expense_number}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {expense.main_category_name || expense.category_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                          {expense.description}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-right">
                          ₦{parseFloat(expense.amount || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              expense.approval_status === 'posted'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                                : expense.approval_status === 'approved'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                  : expense.approval_status === 'rejected'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            }`}
                          >
                            {expense.approval_status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Cost Breakdown */}
              <div className="bg-emerald-50 dark:bg-emerald-900/80 border border-emerald-200 dark:border-emerald-800/30 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 dark:text-emerald-400 mb-3">
                  Total Order Cost (Accounting View)
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Product Total</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ₦{parseFloat(order.total_amount || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">
                      Linked Expenses (
                      {
                        orderExpenses.filter((e) =>
                          ['approved', 'posted'].includes(e.approval_status)
                        ).length
                      }{' '}
                      approved/posted)
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ₦
                      {orderExpenses
                        .filter((e) => ['approved', 'posted'].includes(e.approval_status))
                        .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)
                        .toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-emerald-300 dark:border-emerald-800">
                    <span className="font-semibold text-emerald-800 dark:text-emerald-300">
                      Total Order Cost
                    </span>
                    <span className="font-bold text-emerald-700 dark:text-emerald-400">
                      ₦
                      {(
                        parseFloat(order.total_amount || 0) +
                        orderExpenses
                          .filter((e) => ['approved', 'posted'].includes(e.approval_status))
                          .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0)
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Order Documents Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            Order Documents
          </h3>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            All documents generated for this order. Click on a document to view, download, or send.
          </p>

          {/* Documents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Proforma Invoice */}
            {order.proforma_invoice_number && (
              <button
                onClick={() => setShowProformaPreviewModal(true)}
                className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                  <FileText className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Proforma Invoice
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {order.proforma_invoice_number}
                  </p>
                </div>
                <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 rotate-180 transition-colors" />
              </button>
            )}

            {/* Invoice */}
            {order.invoice_number && (
              <button
                onClick={() => setShowInvoiceModal(true)}
                className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                  <FileText className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Invoice</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {order.invoice_number}
                  </p>
                </div>
                <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 rotate-180 transition-colors" />
              </button>
            )}

            {/* Receipt */}
            {order.receipt_number && (
              <button
                onClick={() => setShowReceiptModal(true)}
                className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 hover:border-emerald-300 dark:hover:border-emerald-600 transition-all text-left group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <Receipt className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Receipt</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {order.receipt_number}
                  </p>
                </div>
                <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 rotate-180 transition-colors" />
              </button>
            )}

            {/* Waybill */}
            {order.waybill_number && (
              <div className="flex items-center gap-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-700/20">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Waybill</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {order.waybill_number}
                  </p>
                </div>
              </div>
            )}

            {/* No Documents */}
            {!order.proforma_invoice_number &&
              !order.invoice_number &&
              !order.receipt_number &&
              !order.waybill_number && (
                <div className="col-span-full text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No documents have been generated for this order yet.
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Cancel / Delete Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Danger Zone</h3>
        <div className="flex flex-wrap gap-3">
          {![
            'loading',
            'loaded',
            'invoiced',
            'dispatched',
            'delivered',
            'completed',
            'cancelled'
          ].includes(orderStatus) && (
            <button
              onClick={handleCancelOrder}
              disabled={updating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" />
              {updating ? 'Cancelling...' : 'Cancel Order'}
            </button>
          )}
          {['loading', 'loaded', 'invoiced', 'dispatched', 'delivered'].includes(orderStatus) && (
            <p className="text-sm text-gray-500">
              ⚠️ Orders cannot be cancelled after loading has started. Use order reassignment
              feature instead.
            </p>
          )}
          <button
            onClick={handleDeleteOrder}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {deleting ? 'Deleting...' : 'Delete Order'}
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-3">
          Deleting an order is permanent and cannot be undone.
        </p>
      </div>

      {/* Company Confirmation Modal */}
      <CompanyConfirmationModal
        isOpen={showCompanyConfirmModal}
        onClose={() => setShowCompanyConfirmModal(false)}
        onConfirm={handleCompanyConfirm}
        order={order}
        loading={updating}
      />

      {/* Proforma & Payment Modal */}
      <ProformaAndPaymentModal
        isOpen={showProformaPaymentModal}
        onClose={() => setShowProformaPaymentModal(false)}
        order={order}
        onSuccess={handleProformaPaymentSuccess}
        onProceedToLoading={handleProceedToLoading}
      />

      {/* Payment Recording Modal (Legacy - kept for backward compatibility) */}
      <PaymentRecordingModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        order={order}
      />

      {/* Invoice Preview Modal */}
      {showInvoiceModal && (
        <InvoicePreviewModal
          order={order}
          invoice={generatedInvoice}
          onClose={handleInvoiceModalClose}
          onSuccess={() => fetchOrder(false)}
        />
      )}

      {/* Proforma Invoice Preview Modal */}
      {showProformaPreviewModal && (
        <ProformaInvoicePreviewModal
          order={order}
          onClose={() => setShowProformaPreviewModal(false)}
          onSuccess={() => fetchOrder(false)}
        />
      )}

      {/* Receipt Preview Modal */}
      {showReceiptModal && (
        <ReceiptPreviewModal
          order={order}
          receipt={generatedReceipt}
          onClose={handleReceiptModalClose}
          onSuccess={() => fetchOrder(false)}
        />
      )}

      {/* Edit Order Modal - Uses CreateOrderModal in display-only mode for now */}
      {showEditModal && (
        <CreateOrderModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchOrder();
            // After edit, proforma may be outdated - user can regenerate
            toast.info('Order updated. You may want to regenerate the proforma invoice.');
          }}
          editOrder={order}
        />
      )}

      {/* Reassign Order Modal */}
      <ReassignOrderModal
        isOpen={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        order={order}
        onSuccess={() => {
          setShowReassignModal(false);
          fetchOrder();
        }}
      />

      {/* Expense Form Modal */}
      <ExpenseFormModal
        isOpen={showExpenseModal}
        onClose={() => {
          setShowExpenseModal(false);
          setSelectedExpense(null);
        }}
        onSuccess={() => {
          fetchOrderExpenses();
          setSelectedExpense(null);
          toast.success(
            selectedExpense ? 'Expense updated successfully' : 'Expense linked to order'
          );
        }}
        expenseId={selectedExpense}
        module="wholesale"
        preSelectedOrderId={parseInt(id)}
        preSelectedDistributorId={order?.distributor_id}
      />

      {/* Expense Detail Modal */}
      <ExpenseDetailModal
        expense={selectedExpense}
        isOpen={showExpenseDetailModal}
        onClose={() => {
          setShowExpenseDetailModal(false);
          setSelectedExpense(null);
        }}
        onEdit={(expense) => {
          setSelectedExpense(expense.id);
          setShowExpenseDetailModal(false);
          setShowExpenseModal(true);
        }}
        onApprove={handleApproveExpense}
        onReject={handleRejectExpense}
        onDelete={handleDeleteExpense}
        onPostToGL={handlePostToGL}
        isAdmin={isAdmin}
        user={user}
      />
    </div>
  );
};

export default WholesaleOrderDetailPage;
